import React from 'react'
import { toTitleCase } from '@mobi/utils/string'
import type { BamAccountHolder } from '@mobi/api-types'
import { Paragraph } from '@core/Components/Text/Paragraph'
import { ButtonBlock } from '@mobi/component-library/Common/Buttons'
import { Grid, GridRow, GridCell } from '@mobi/component-library/Common/Grid'
import { NoticeBoxSingle } from '@core/Components/NoticeBox'
import { InfoCopyStyled as InfoCopy } from '../DepositLimitsAnniversaryPopup/DepositLimitsAnniversaryPopup.styles'

export interface AnniversaryPopupContentProps {
  isDesktop?: boolean
  accountHolder: BamAccountHolder | null
  onKeepLimit(): void
  onChangeLimit(): void
}

export const AnniversaryPopupContent = ({
  isDesktop,
  accountHolder,
  onKeepLimit,
  onChangeLimit,
}: AnniversaryPopupContentProps) => {
  if (accountHolder === null) {
    return null
  }

  return (
    <>
      <Paragraph data-tid-anniversary-introduction='' isDesktop={isDesktop}>
        <strong>Hi {accountHolder.FullName}</strong>, to continue betting, please review your
        deposit limit.
      </Paragraph>
      <NoticeBoxSingle
        title={
          <Paragraph isDesktop={isDesktop} data-tid-current-limit=''>
            <CurrentLimit {...accountHolder} />
          </Paragraph>
        }
        noticeBoxType='Info'
        hasBorder
        testId='current-limit'
        isDesktop={isDesktop}
      />
      <Grid padding='0.4rem'>
        <GridRow>
          <GridCell>
            <ButtonBlock
              onClick={onKeepLimit}
              color='primary'
              testId='anniversary-keep'
              isDesktop={isDesktop}
            >
              Keep
            </ButtonBlock>
          </GridCell>
        </GridRow>
        <GridRow>
          <GridCell>
            <ButtonBlock
              onClick={onChangeLimit}
              color='secondary'
              testId='anniversary-change'
              isDesktop={isDesktop}
            >
              Change
            </ButtonBlock>
          </GridCell>
        </GridRow>
      </Grid>
      <InfoCopy isDesktop={isDesktop} data-tid-why-ask=''>
        <strong>Why do we ask this?</strong>
        <br />
        At TABtouch we support responsible wagering. To help you manage your betting, this is your
        annual reminder to review your deposit limit. You can also review this at any time by
        visiting deposit limit in My Account menu.
      </InfoCopy>
    </>
  )
}

type ExistingDepositLimitProps = Pick<
  BamAccountHolder,
  'PreCommitmentMaxDepositAmount' | 'PreCommitmentMaxDepositFrequency'
>

const CurrentLimit = ({
  PreCommitmentMaxDepositAmount,
  PreCommitmentMaxDepositFrequency,
}: ExistingDepositLimitProps) => {
  return PreCommitmentMaxDepositAmount && PreCommitmentMaxDepositFrequency ? (
    <ExistingDepositLimit
      PreCommitmentMaxDepositAmount={PreCommitmentMaxDepositAmount}
      PreCommitmentMaxDepositFrequency={PreCommitmentMaxDepositFrequency}
    />
  ) : (
    <NoDepositLimit />
  )
}

export const ExistingDepositLimit = ({
  PreCommitmentMaxDepositAmount,
  PreCommitmentMaxDepositFrequency,
}: ExistingDepositLimitProps) => {
  return (
    <span data-tid-current-value>
      Currently set to{' '}
      <strong>
        ${PreCommitmentMaxDepositAmount} {toTitleCase(PreCommitmentMaxDepositFrequency || '')}
      </strong>
    </span>
  )
}

export const NoDepositLimit = () => {
  return (
    <span data-tid-no-limit=''>
      <strong>No deposit limit set</strong>
    </span>
  )
}
